import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  useTheme,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  TrendingUp,
  Calendar,
  Users,
  Target,
  RefreshCw,
  Filter,
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { toast } from 'react-toastify';

import StatsCard from '../Components/UI/StatsCard';
import { ElevatedCard } from '../Components/UI/EnhancedCard';
import FullPageLoader from '../Components/UI/FullPageLoader';
import { ModernPieChart, ModernBarChart } from '../Components/Charts';

import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';

const STATUS_COLORS = {
  active: '#3b82f6',
  completed: '#22c55e',
  cancelled: '#ef4444',
};

const DUE_DATE_COLORS = {
  ontime: '#22c55e',
  breached: '#ef4444',
  atrisk: '#f59e0b',
};

function TaskDashboard() {
  const theme = useTheme();
  const { selectedOrgId, loading: orgLoading } = useOrg();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Filters
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [dateRange, setDateRange] = useState('30'); // days

  // Fetch sites
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const user = getAuth().currentUser;
        const token = await user.getIdToken();
        const response = await axios.get(
          `${BASE_URL}/api/sites?organisation_id=${selectedOrgId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSites(response.data || []);
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };

    if (!orgLoading && selectedOrgId) {
      fetchSites();
    }
  }, [selectedOrgId, orgLoading]);

  // Fetch dashboard data
  const dashboardDataSeqRef = useRef(0);
  const fetchDashboardData = async () => {
    if (orgLoading || !selectedOrgId) return;
    const seq = ++dashboardDataSeqRef.current;

    setRefreshing(true);
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      let query = `organisation_id=${selectedOrgId}&days=${dateRange}`;
      if (selectedSite) {
        query += `&site_id=${selectedSite}`;
      }

      const response = await axios.get(
        `${BASE_URL}/api/task-dashboard?${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (seq === dashboardDataSeqRef.current) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error('Error fetching task dashboard:', error);
      toast.error('Failed to load task dashboard');
    } finally {
      if (seq === dashboardDataSeqRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedOrgId, selectedSite, dateRange, orgLoading]);

  // Prepare chart data
  const statusChartData = useMemo(() => {
    if (!dashboardData?.status_breakdown) return [];
    return Object.entries(dashboardData.status_breakdown).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' '),
      value: count,
      color: STATUS_COLORS[status] || '#6b7280',
    }));
  }, [dashboardData]);

  const dueDateChartData = useMemo(() => {
    const bd = dashboardData?.due_date_breakdown;
    if (!bd) return [];
    return Object.entries(bd).map(([status, count]) => ({
      name: status === 'ontime' ? 'On Time' : status === 'breached' ? 'Breached' : 'At Risk',
      value: count,
      color: DUE_DATE_COLORS[status] || '#6b7280',
    }));
  }, [dashboardData]);

  const workflowChartData = useMemo(() => {
    const bd = dashboardData?.workflow_breakdown;
    if (!bd) return [];
    return bd.map(cat => ({
      name: cat.category || 'No Workflow',
      value: parseInt(cat.count),
      color: theme.palette.primary.main,
    }));
  }, [dashboardData, theme]);

  const currentStageChartData = useMemo(() => {
    if (!dashboardData?.current_stage_breakdown) return [];
    return dashboardData.current_stage_breakdown.map(row => ({
      name: row.stage_name,
      value: row.count,
    }));
  }, [dashboardData]);

  const weeklyTrendData = useMemo(() => {
    if (!dashboardData?.weekly_trend) return [];
    return dashboardData.weekly_trend.map(week => ({
      name: week.week_label || week.week,
      completed: week.completed || 0,
      created: week.created || 0,
    }));
  }, [dashboardData]);

  // V8 extensions
  const siteStageMatrix = useMemo(() => {
    const rows = dashboardData?.site_stage_matrix || [];
    const bySite = {};
    rows.forEach(r => { (bySite[r.site_name] = bySite[r.site_name] || []).push(r); });
    return bySite;
  }, [dashboardData]);

  const visitOutcomeChartData = useMemo(() => {
    const o = dashboardData?.visit_outcomes;
    if (!o || (o.on_time + o.delayed + o.missed) === 0) return [];
    return [
      { name: 'On time', value: o.on_time, color: STATUS_COLORS.completed },
      { name: 'Delayed', value: o.delayed, color: '#f59e0b' },
      { name: 'Missed', value: o.missed, color: STATUS_COLORS.cancelled },
    ];
  }, [dashboardData]);

  const bottleneckChartData = useMemo(() => {
    if (!dashboardData?.stage_bottlenecks) return [];
    return dashboardData.stage_bottlenecks.map(row => ({
      label: row.stage_name,
      avg_actual_days: row.avg_actual_days || 0,
      expected_days: row.expected_days || 0,
    }));
  }, [dashboardData]);

  if (loading) {
    return <FullPageLoader message="Loading Task Dashboard..." />;
  }

  const stats = dashboardData || {
    total_tasks: 0,
    active_tasks: 0,
    completed_tasks: 0,
    cancelled_tasks: 0,
    on_time_delivery_rate: 0,
    avg_completion_time: 0,
    overdue_tasks: 0,
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Task Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and analyze task performance
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Date Range Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={dateRange}
              label="Time Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="365">Last year</MenuItem>
            </Select>
          </FormControl>

          {/* Site Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Site</InputLabel>
            <Select
              value={selectedSite}
              label="Site"
              onChange={(e) => setSelectedSite(e.target.value)}
            >
              <MenuItem value="">All Sites</MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.site_id || site.id} value={site.site_id || site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchDashboardData} disabled={refreshing}>
              <RefreshCw className={refreshing ? 'animate-spin' : ''} size={20} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {refreshing && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Tasks"
            value={stats.total_tasks}
            icon={<Target size={24} />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Active"
            value={stats.active_tasks}
            icon={<Clock size={24} />}
            color={STATUS_COLORS.active}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Completed"
            value={stats.completed_tasks}
            icon={<CheckCircle size={24} />}
            color={STATUS_COLORS.completed}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Overdue"
            value={stats.overdue_tasks}
            icon={<AlertTriangle size={24} />}
            color={STATUS_COLORS.cancelled}
          />
        </Grid>
      </Grid>

      {/* V8: Maintenance / stall KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Stalled Stages"
            value={dashboardData?.stalled_stages || 0}
            icon={<AlertTriangle size={24} />}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Unassigned Visits"
            value={dashboardData?.unassigned_visits || 0}
            icon={<Clock size={24} />}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Missed Visits (30d)"
            value={dashboardData?.missed_visits_30d || 0}
            icon={<AlertTriangle size={24} />}
            color={STATUS_COLORS.cancelled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Maintenance Compliance"
            value={dashboardData?.maintenance_compliance_rate != null ? `${dashboardData.maintenance_compliance_rate}%` : '—'}
            icon={<CheckCircle size={24} />}
            color={STATUS_COLORS.completed}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Vendor Crew Seats"
            value={dashboardData?.vendor_seats || 0}
            icon={<Users size={24} />}
            color={theme.palette.primary.main}
          />
        </Grid>
      </Grid>

      {/* V8: Establishment progress (site x stage matrix) */}
      {Object.keys(siteStageMatrix).length > 0 && (
        <ElevatedCard sx={{ mb: 3 }}>
          <Box sx={{ p: 2, overflowX: 'auto' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Establishment Progress
            </Typography>
            {Object.entries(siteStageMatrix).map(([siteName, rows]) => (
              <Box key={siteName} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={500} sx={{ minWidth: 140 }}>{siteName}</Typography>
                {rows.map(r => {
                  const stalled = r.status === 'in_progress' && r.elapsed_days != null && r.elapsed_days > r.expected_days;
                  const label = r.status === 'completed' || r.status === 'skipped' ? '✅' : stalled ? `⚠ ${r.elapsed_days}d` : r.status === 'in_progress' ? `⏳ ${r.elapsed_days ?? 0}d` : '—';
                  return (
                    <Chip
                      key={r.position}
                      label={`${r.stage_name}: ${label}`}
                      size="small"
                      color={stalled ? 'error' : r.status === 'completed' || r.status === 'skipped' ? 'success' : 'default'}
                    />
                  );
                })}
              </Box>
            ))}
          </Box>
        </ElevatedCard>
      )}

      {/* V8: Visit outcomes + stage bottlenecks */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {visitOutcomeChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <ElevatedCard>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Maintenance Visit Outcomes
                </Typography>
                <ModernPieChart data={visitOutcomeChartData} height={280} />
              </Box>
            </ElevatedCard>
          </Grid>
        )}
        {bottleneckChartData.length > 0 && (
          <Grid item xs={12} md={visitOutcomeChartData.length > 0 ? 6 : 12}>
            <ElevatedCard>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Stage Bottlenecks (avg actual vs expected days)
                </Typography>
                <ModernBarChart
                  data={bottleneckChartData}
                  xAxisKey="label"
                  height={280}
                  bars={[
                    { dataKey: 'avg_actual_days', name: 'Actual', color: theme.palette.warning.main },
                    { dataKey: 'expected_days', name: 'Expected', color: theme.palette.primary.main },
                  ]}
                />
              </Box>
            </ElevatedCard>
          </Grid>
        )}
      </Grid>

      {/* Due-Date & Performance */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                On-Time Delivery
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {Math.round(stats.on_time_delivery_rate || 0)}%
                </Typography>
                <Chip
                  label={stats.on_time_delivery_rate >= 90 ? 'Excellent' : stats.on_time_delivery_rate >= 75 ? 'Good' : 'Needs Improvement'}
                  color={stats.on_time_delivery_rate >= 90 ? 'success' : stats.on_time_delivery_rate >= 75 ? 'warning' : 'error'}
                  size="small"
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={stats.on_time_delivery_rate || 0}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    bgcolor: stats.on_time_delivery_rate >= 90 ? 'success.main' : stats.on_time_delivery_rate >= 75 ? 'warning.main' : 'error.main',
                  },
                }}
              />
            </Box>
          </ElevatedCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Avg. Completion Time
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {stats.avg_completion_time || 0}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  hours
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Average time to complete tasks
              </Typography>
            </Box>
          </ElevatedCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Task Completion Rate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {stats.total_tasks > 0 ? Math.round((stats.completed_tasks / stats.total_tasks) * 100) : 0}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {stats.completed_tasks} of {stats.total_tasks} tasks completed
              </Typography>
            </Box>
          </ElevatedCard>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Status Breakdown */}
        <Grid item xs={12} md={6}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Task Status Distribution
              </Typography>
              {statusChartData.length > 0 ? (
                <ModernPieChart data={statusChartData} height={300} />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Due-Date Compliance */}
        <Grid item xs={12} md={6}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Due-Date Compliance
              </Typography>
              {dueDateChartData.length > 0 ? (
                <ModernPieChart data={dueDateChartData} height={300} />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Task Trend */}
        <Grid item xs={12}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Task Trend
              </Typography>
              {weeklyTrendData.length > 0 ? (
                <ModernBarChart 
                  data={weeklyTrendData} 
                  xAxisKey="name"
                  bars={[
                    { dataKey: 'created', name: 'Created', color: theme.palette.primary.main },
                    { dataKey: 'completed', name: 'Completed', color: theme.palette.success.main }
                  ]}
                  height={300}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No trend data available</Typography>
                </Box>
              )}
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Workflow Breakdown */}
        <Grid item xs={12} md={6}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Tasks by Workflow
              </Typography>
              {workflowChartData.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {workflowChartData.map((wf, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{wf.name}</Typography>
                      </Box>
                      <Box sx={{ width: 100, mx: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(wf.value / Math.max(...workflowChartData.map(c => c.value))) * 100}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
                        {wf.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary">No workflow data available</Typography>
                </Box>
              )}
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Current Stage Breakdown */}
        <Grid item xs={12} md={6}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Active Tasks by Stage
              </Typography>
              {currentStageChartData.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {currentStageChartData.map((row, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
                      </Box>
                      <Box sx={{ width: 100, mx: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(row.value / Math.max(...currentStageChartData.map(r => r.value))) * 100}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary">No active tasks</Typography>
                </Box>
              )}
            </Box>
          </ElevatedCard>
        </Grid>

        {/* Top Staff Performance */}
        <Grid item xs={12} md={6}>
          <ElevatedCard>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Top Performers
              </Typography>
              {dashboardData?.top_performers?.length > 0 ? (
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {dashboardData.top_performers.map((performer, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, p: 1, bgcolor: index === 0 ? 'primary.50' : 'transparent', borderRadius: 1 }}>
                      <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        bgcolor: index === 0 ? 'primary.main' : index === 1 ? 'grey.400' : 'grey.300',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        mr: 2
                      }}>
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{performer.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {performer.completed_tasks} tasks completed
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${performer.completion_rate || 0}%`}
                        size="small"
                        color={Number(performer.completion_rate) >= 90 ? 'success' : Number(performer.completion_rate) >= 75 ? 'warning' : 'error'}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary">No performer data available</Typography>
                </Box>
              )}
            </Box>
          </ElevatedCard>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TaskDashboard;
